import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { setSuperadminViewStore } from '../../utils/superadminStoreView';
import { adminApi } from '../../services/adminApi';
import type { StoreListItem } from '../../types/admin';
import {
  ActiveBadge,
  AdminEmpty,
  AdminLoading,
  AdminPageHeader,
  formatDate,
} from './adminShared';
import './AdminLayout.css';

const AdminStoresList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StoreListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters: { q?: string; active?: boolean } = {};
      if (q.trim()) filters.q = q.trim();
      if (activeFilter === 'true') filters.active = true;
      if (activeFilter === 'false') filters.active = false;

      const data = await adminApi.listStores(filters);
      setItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando tiendas');
    } finally {
      setLoading(false);
    }
  }, [q, activeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <AdminPageHeader title="Tiendas" actionLabel="+ Nueva tienda" actionTo="/admin/stores/new" />

      <div className="admin-toolbar">
        <input
          className="admin-input"
          placeholder="Buscar por nombre o store_id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="admin-select"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as 'all' | 'true' | 'false')}
        >
          <option value="all">Todas</option>
          <option value="true">Activas</option>
          <option value="false">Inactivas</option>
        </select>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={load}>
          Buscar
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {loading ? (
        <AdminLoading />
      ) : items.length === 0 ? (
        <AdminEmpty message="No se encontraron tiendas." />
      ) : (
        <div className="admin-card admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tienda</th>
                <th>Store ID</th>
                <th>País</th>
                <th>Estado</th>
                <th>Usuarios</th>
                <th>Último sync</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((store) => (
                <tr key={store.store_id}>
                  <td>
                    <Link to={`/admin/stores/${store.store_id}`}>{store.store_name}</Link>
                  </td>
                  <td>{store.store_id}</td>
                  <td>{store.country || '—'}</td>
                  <td>
                    <ActiveBadge active={store.active} />
                  </td>
                  <td>{store.user_count ?? 0}</td>
                  <td>{formatDate(store.last_boris_sync)}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-btn-link"
                      onClick={() => {
                        setSuperadminViewStore({
                          storeId: store.store_id,
                          storeName: store.store_name,
                          storeActive: store.active,
                        });
                        navigate('/dashboard');
                      }}
                    >
                      Ver dashboard
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default AdminStoresList;
